import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  InventoryService,
  type VariantValueDto
} from '../services';
import GuideButton from '../../../common/guide/GuideButton';

interface VariantDictionaryModalProps {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
  /**
   * Modal acildiginda otomatik secilecek tip (varsa).
   * Matris ekranindan "yeni deger ekle" yapilirken tipin onceden secili gelmesi icin kullanilir.
   */
  initialType?: string;
}

// Kullanici "Grup" kavramini gormesin diye butun tipler ve degerler
// gizli/varsayilan tek bir grup uzerinde tutulur. Backend hala grup bekliyor.
const DEFAULT_GROUP_NAME = 'Genel';

const parseApiError = (error: any, fallback: string): string => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const directMessage =
    data?.message ||
    data?.Message ||
    data?.error ||
    data?.Error ||
    data?.title ||
    data?.Title;

  if (directMessage && typeof directMessage === 'string') {
    return directMessage;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const firstKey = Object.keys(data.errors)[0];
    const firstError = firstKey ? data.errors[firstKey]?.[0] : null;
    if (firstError) return firstError;
  }

  if (status === 404) {
    return 'İlgili API endpoint bulunamadı. Backend servisini yeniden başlatıp tekrar deneyin.';
  }

  if (status === 401 || status === 403) {
    return 'Yetki hatası oluştu. Oturum/şirket bilgisini kontrol edin.';
  }

  return fallback;
};

export const VariantDictionaryModal: React.FC<VariantDictionaryModalProps> = ({
  open,
  onClose,
  onChanged,
  initialType
}) => {
  const [defaultGroupId, setDefaultGroupId] = useState('');
  const [values, setValues] = useState<VariantValueDto[]>([]);
  // Henuz hicbir degeri olmayan, oturum icinde elle eklenen tipler.
  // Ilk degeri kaydedilince zaten values uzerinden gorunecegi icin listeden cikarilir.
  const [customTypes, setCustomTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [valueForm, setValueForm] = useState({
    id: '',
    valueName: '',
    valueCode: '',
    sortOrder: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    values.forEach(v => {
      if (v.valueType?.trim()) set.add(v.valueType.trim());
    });
    customTypes.forEach(t => {
      if (t?.trim()) set.add(t.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [values, customTypes]);

  const typeValues = useMemo(
    () =>
      values
        .filter(v => v.valueType === selectedType)
        .sort((a, b) => {
          if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          }
          return (a.valueName || '').localeCompare(b.valueName || '', 'tr');
        }),
    [values, selectedType]
  );

  const getNextSortOrderForType = (valueType: string): number => {
    const max = values
      .filter(v => v.valueType === valueType)
      .reduce((m, v) => Math.max(m, Number(v.sortOrder) || 0), 0);
    return max + 1;
  };

  // Modal acildiginda: varsayilan grubu hazirla, degerleri yukle.
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const groups = await InventoryService.getVariantGroups();
        let groupId =
          groups.find(g => g.groupName === DEFAULT_GROUP_NAME)?.id ||
          groups[0]?.id ||
          '';

        if (!groupId) {
          // Hicbir grup yoksa kullaniciya gostermeden varsayilan grubu olustur.
          const created = await InventoryService.createVariantGroup({
            groupName: DEFAULT_GROUP_NAME,
            description: 'Varsayilan varyant grubu (otomatik olusturuldu).',
            isActive: true
          });
          groupId = created.id;
        }

        if (!mounted) return;
        setDefaultGroupId(groupId);

        const vals = await InventoryService.getVariantValuesByGroup(groupId);
        if (!mounted) return;
        setValues(vals);

        // Tip secimi: oncelik initialType, sonra var olan ilk tip.
        if (initialType && initialType.trim()) {
          const t = initialType.trim();
          setSelectedType(t);
          if (!vals.some(v => v.valueType === t)) {
            setCustomTypes(prev => (prev.includes(t) ? prev : [...prev, t]));
          }
        } else {
          const firstType = vals[0]?.valueType || '';
          setSelectedType(firstType);
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(parseApiError(e, 'Varyant verileri yüklenemedi.'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialType]);

  // Modal kapaninca lokal state sifirlansin.
  useEffect(() => {
    if (!open) {
      setCustomTypes([]);
      setSelectedType('');
      setNewTypeName('');
      setValueForm({ id: '', valueName: '', valueCode: '', sortOrder: 0, isActive: true });
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const reloadValues = async () => {
    if (!defaultGroupId) return;
    const vals = await InventoryService.getVariantValuesByGroup(defaultGroupId);
    setValues(vals);
  };

  const resetValueForm = () => {
    setValueForm({ id: '', valueName: '', valueCode: '', sortOrder: 0, isActive: true });
  };

  const handleAddNewType = () => {
    const t = newTypeName.trim();
    if (!t) {
      setError('Tip adı boş olamaz.');
      return;
    }
    if (availableTypes.some(x => x.toLowerCase() === t.toLowerCase())) {
      setError('Bu tip zaten var.');
      setSelectedType(availableTypes.find(x => x.toLowerCase() === t.toLowerCase()) || t);
      setNewTypeName('');
      return;
    }
    setCustomTypes(prev => [...prev, t]);
    setSelectedType(t);
    setNewTypeName('');
    setError(null);
    setSuccess(`"${t}" tipi eklendi. İlk değeri eklediğinizde kalıcı olur.`);
    resetValueForm();
  };

  const handleSaveValue = async () => {
    try {
      setError(null);
      setSuccess(null);
      if (!defaultGroupId) {
        setError('Varsayılan grup bulunamadı.');
        return;
      }
      if (!selectedType) {
        setError('Önce sol panelden tip seçin veya yeni tip ekleyin.');
        return;
      }
      if (!valueForm.valueName.trim()) {
        setError('Değer adı zorunludur.');
        return;
      }

      if (valueForm.id) {
        await InventoryService.updateVariantValue({
          id: valueForm.id,
          variantGroupId: defaultGroupId,
          valueType: selectedType,
          valueName: valueForm.valueName.trim(),
          valueCode: valueForm.valueCode.trim() || undefined,
          sortOrder: Number(valueForm.sortOrder) || 0,
          isActive: valueForm.isActive
        });
        setSuccess('Değer güncellendi.');
      } else {
        await InventoryService.createVariantValue({
          variantGroupId: defaultGroupId,
          valueType: selectedType,
          valueName: valueForm.valueName.trim(),
          valueCode: valueForm.valueCode.trim() || undefined,
          sortOrder: getNextSortOrderForType(selectedType),
          isActive: valueForm.isActive
        });
        setSuccess('Değer eklendi.');
      }

      await reloadValues();
      resetValueForm();
      // Tip artik kalici (en az bir degeri var); custom listeden cikar.
      setCustomTypes(prev => prev.filter(t => t !== selectedType));
      if (onChanged) onChanged();
    } catch (e: any) {
      setError(parseApiError(e, 'Değer kaydedilemedi.'));
    }
  };

  /**
   * Bir tipin tum degerlerini 1..N olacak sekilde yeniden sirala.
   * Sadece sortOrder degisen kayitlara update gonderir (gereksiz trafik olmasin).
   */
  const resequenceType = async (groupId: string, type: string, allValues: VariantValueDto[]) => {
    const sameType = allValues
      .filter(v => v.valueType === type)
      .sort((a, b) => {
        if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        }
        return (a.valueName || '').localeCompare(b.valueName || '', 'tr');
      });

    for (let i = 0; i < sameType.length; i++) {
      const desired = i + 1;
      const v = sameType[i];
      if ((v.sortOrder || 0) !== desired) {
        await InventoryService.updateVariantValue({
          id: v.id,
          variantGroupId: groupId,
          valueType: v.valueType,
          valueName: v.valueName,
          valueCode: v.valueCode,
          sortOrder: desired,
          isActive: v.isActive
        });
      }
    }
  };

  const handleDeleteValue = async (id: string) => {
    if (!window.confirm('Bu değeri silmek istediğinize emin misiniz?')) return;
    try {
      setError(null);
      setSuccess(null);

      const deletedType = values.find(v => v.id === id)?.valueType || selectedType;

      await InventoryService.deleteVariantValue({ id });

      // Silme sonrasi: ayni tipin kalan degerlerini 1..N olacak sekilde yeniden numaralandir.
      let resequenced = false;
      if (defaultGroupId && deletedType) {
        const fresh = await InventoryService.getVariantValuesByGroup(defaultGroupId);
        await resequenceType(defaultGroupId, deletedType, fresh);
        resequenced = true;
      }

      await reloadValues();
      if (valueForm.id === id) resetValueForm();
      setSuccess(resequenced ? 'Değer silindi ve sıra güncellendi.' : 'Değer silindi.');
      if (onChanged) onChanged();
    } catch (e: any) {
      setError(parseApiError(e, 'Değer silinemedi.'));
    }
  };

  const handleDeleteType = async () => {
    if (!selectedType) return;
    const toDelete = values.filter(v => v.valueType === selectedType);
    const msg =
      toDelete.length > 0
        ? `"${selectedType}" tipini ve ${toDelete.length} değerini silmek istediğinize emin misiniz?`
        : `"${selectedType}" tipini listeden kaldırmak istediğinize emin misiniz?`;
    if (!window.confirm(msg)) return;

    try {
      setError(null);
      setSuccess(null);
      for (const v of toDelete) {
        await InventoryService.deleteVariantValue({ id: v.id });
      }
      setCustomTypes(prev => prev.filter(t => t !== selectedType));
      const remaining = availableTypes.filter(t => t !== selectedType);
      setSelectedType(remaining[0] || '');
      resetValueForm();
      await reloadValues();
      setSuccess('Tip silindi.');
      if (onChanged) onChanged();
    } catch (e: any) {
      setError(parseApiError(e, 'Tip silinemedi.'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Varyant Sözlüğü
        <GuideButton guideKey="inventory.variantDictionary" size="small" />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* SOL: Tipler */}
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Tipler
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder="Yeni tip (örn. Renk)"
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewType();
                      }
                    }}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddNewType}
                    startIcon={<AddIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Ekle
                  </Button>
                </Stack>

                {availableTypes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Yükleniyor...' : 'Henüz tip yok. Yukarıdan ekleyin.'}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {availableTypes.map(type => {
                      const count = values.filter(v => v.valueType === type).length;
                      const isCustom = count === 0;
                      return (
                        <ListItemButton
                          key={type}
                          selected={selectedType === type}
                          onClick={() => {
                            setSelectedType(type);
                            resetValueForm();
                          }}
                        >
                          <ListItemText
                            primary={type}
                            secondary={isCustom ? 'Henüz değeri yok' : `${count} değer`}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}

                {selectedType && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteType}
                    >
                      Tipi Sil
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* SAG: Secili tipin degerleri */}
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {selectedType ? `"${selectedType}" Değerleri` : 'Önce bir tip seçin'}
                </Typography>

                {selectedType && (
                  <>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mb: 2 }}
                      alignItems={{ sm: 'flex-start' }}
                    >
                      <TextField
                        size="small"
                        label="Değer (örn. Kırmızı)"
                        value={valueForm.valueName}
                        onChange={(e) =>
                          setValueForm(prev => ({ ...prev, valueName: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveValue();
                          }
                        }}
                        sx={{ flex: 2 }}
                      />
                      <TextField
                        size="small"
                        label="Kod (ops.)"
                        value={valueForm.valueCode}
                        onChange={(e) =>
                          setValueForm(prev => ({ ...prev, valueCode: e.target.value }))
                        }
                        sx={{ flex: 1 }}
                      />
                      {valueForm.id && (
                        <TextField
                          size="small"
                          type="number"
                          label="Sıra"
                          value={valueForm.sortOrder}
                          onChange={(e) =>
                            setValueForm(prev => ({
                              ...prev,
                              sortOrder: Number(e.target.value || 0)
                            }))
                          }
                          sx={{ width: 90 }}
                        />
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" size="small" onClick={handleSaveValue}>
                          {valueForm.id ? 'Güncelle' : 'Ekle'}
                        </Button>
                        {valueForm.id && (
                          <Button variant="outlined" size="small" onClick={resetValueForm}>
                            İptal
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    {typeValues.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Bu tip için henüz değer yok. Yukarıdaki formdan ilk değeri ekleyin.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Değer</TableCell>
                              <TableCell>Kod</TableCell>
                              <TableCell>Sıra</TableCell>
                              <TableCell>Durum</TableCell>
                              <TableCell align="right">İşlem</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {typeValues.map(v => (
                              <TableRow key={v.id} hover selected={valueForm.id === v.id}>
                                <TableCell>{v.valueName}</TableCell>
                                <TableCell>{v.valueCode || '-'}</TableCell>
                                <TableCell>{v.sortOrder}</TableCell>
                                <TableCell>{v.isActive ? 'Aktif' : 'Pasif'}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Düzenle">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setValueForm({
                                          id: v.id,
                                          valueName: v.valueName,
                                          valueCode: v.valueCode || '',
                                          sortOrder: v.sortOrder,
                                          isActive: v.isActive
                                        })
                                      }
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Sil">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteValue(v.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};
